import type { AxiosError } from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { api } from '../../api';
import { environment } from '../../environment/environment';
import type { Org } from '../../helpers/device.helper';
import { getDidOnboarding, getOrgCode } from '../../helpers/device.helper';
import { ChatPage } from '../../models/chat';
import type { ErrorMessage } from '../../models/Error';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun';

const { logError } = RaygunErrorHandlerService();

export const APP_STATUS = {
  APP_ERROR: 'error',
  APP_EXPIRED: 'expired',
  APP_LATEST: 'latest',
  APP_START: 'start',
  APP_UPDATE_AVAILABLE: 'update_available',
} as const;

export type APP_STATUS = (typeof APP_STATUS)[keyof typeof APP_STATUS];

export const APP_MODAL = {
  ONBOARDING: 'onboarding',
  IDLE: 'idle',
  PERMISSION: 'permission',
  UPDATE: 'update',
  ANDROID_SILENT_PROMPT: 'android_silent_prompt',
} as const;

export type APP_MODAL = (typeof APP_MODAL)[keyof typeof APP_MODAL];

type AppStatusSliceType = {
  appStatus: APP_STATUS;
  appModal: APP_MODAL;
  showNoInternetErrorToast: boolean;
  showNetworkErrorToast: boolean;
  showExpiredTokenErrorToast: boolean;
  showLanguageModal: boolean;
  showAreaModal: boolean;
  showLogoutModal: boolean;
  settingsGoBackPage: ChatPage;
  appOrgCode: Org;
  appUpdateDismissed: boolean;
};

const initialState: AppStatusSliceType = {
  appModal: !getDidOnboarding() ? APP_MODAL.ONBOARDING : APP_MODAL.IDLE,
  appStatus: APP_STATUS.APP_START,
  showNoInternetErrorToast: false,
  showNetworkErrorToast: false,
  showExpiredTokenErrorToast: false,
  showLanguageModal: false,
  showAreaModal: false,
  showLogoutModal: false,
  settingsGoBackPage: ChatPage.LEPS,
  appOrgCode: getOrgCode(),
  appUpdateDismissed: false,
};

export const checkUpgrade = createAsyncThunk<APP_STATUS, undefined, AsyncThunkConfig>(
  'appStatus/checkUpgrade',
  async (_, thunkAPI) => {
    try {
      const response = await api.post<APP_STATUS>('/utility/v0_upgrade', {
        app_version: environment.versionNumber,
      });
      return response ?? APP_STATUS.APP_START;
    } catch (e) {
      logError(e, ['appStatusSlice', 'checkUpgrade']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const setLanguage = createAsyncThunk<
  boolean | undefined,
  { onboarding: boolean; language_code: string },
  AsyncThunkConfig
>('appStatus/setLanguage', async ({ onboarding, language_code }, thunkAPI) => {
  try {
    const response = await api.post<boolean>('/guest/v0_set_language', { onboarding, language_code });
    return response;
  } catch (e) {
    logError(e, ['appStatusSlice', 'setLanguage']);
    return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
  }
});

export const appStatusSlice = createSlice({
  name: 'appStatus',
  initialState,
  reducers: {
    setAppStatus: (state, action: PayloadAction<APP_STATUS>) => {
      state.appStatus = action.payload;
      if (action.payload === APP_STATUS.APP_START) {
        state.appModal = APP_MODAL.IDLE;
      }
    },
    setAppModal: (state, action: PayloadAction<APP_MODAL>) => {
      state.appModal = action.payload;
    },
    setAppUpdateDismissed: (state, action: PayloadAction<boolean>) => {
      state.appUpdateDismissed = action.payload;
    },
    setNoInternetErrorToast: (state, action: PayloadAction<boolean>) => {
      state.showNoInternetErrorToast = action.payload;
    },
    setNetworkErrorToast: (state, action: PayloadAction<boolean>) => {
      state.showNetworkErrorToast = action.payload;
    },
    setExpiredTokenErrorToast: (state, action: PayloadAction<boolean>) => {
      state.showExpiredTokenErrorToast = action.payload;
    },
    setShowLanguageModal: (state, action: PayloadAction<boolean>) => {
      state.showLanguageModal = action.payload;
    },
    setShowAreaModal: (state, action: PayloadAction<boolean>) => {
      state.showAreaModal = action.payload;
    },
    setSettingsGoBackPage: (state, action: PayloadAction<ChatPage>) => {
      state.settingsGoBackPage = action.payload;
    },
    setShowLogoutModal: (state, action: PayloadAction<boolean>) => {
      state.showLogoutModal = action.payload;
    },
    setAppOrgCode: (state, action: PayloadAction<Org>) => {
      state.appOrgCode = action.payload;
      environment.orgCode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(checkUpgrade.fulfilled, (state, action) => {
      state.appStatus = action.payload;
    });
  },
});

export const {
  setAppStatus,
  setAppModal,
  setAppUpdateDismissed,
  setNoInternetErrorToast,
  setNetworkErrorToast,
  setExpiredTokenErrorToast,
  setShowLanguageModal,
  setShowAreaModal,
  setSettingsGoBackPage,
  setShowLogoutModal,
  setAppOrgCode,
} = appStatusSlice.actions;
